import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalyticsFilters } from '../../contexts/AnalyticsFiltersContext';
import useStudentsMap from '../../hooks/useStudentsMap';
import analyticsService from '../../services/analyticsService';
import { ANNOTATION_ITEM_TYPE, RADAR_ALERT_STATUS } from '../../types/radar.constants';
import { getSeverityFromErrorRate } from '../../utils/radarUtils';

import { MOCK_STUDENTS_MAP } from '../../mocks/mockStudents';
import type { AnnotationItem, AnnotationItemType, AnnotationsResponse, RadarAlert, RadarSeverity } from '../../types/radar';
import type { DrawerNavigation } from './AlertDetailDrawer/AlertDetailDrawer';
import { getMockAnnotations, MOCK_COURSE_ID_TO_GROUP_NAME } from '../../mocks/mockRadarData';

type RadarTab = {
  id: string;
  label: string;
  count: number;
};

type SelectOption = {
  label: string;
  value: string;
};

type RadarSortOption = 'relevance' | 'date_newest' | 'date_oldest';

type RadarCategoryFilter = 'all' | AnnotationItemType;

type EmptyStateConfig = {
  iconType: string;
  iconVariant: 'success' | 'warning' | 'muted';
  title: string;
  description: string;
};

type UseRadarViewReturn = {
  activeTab: string;
  alerts: RadarAlert[];
  categoryOptions: SelectOption[];
  drawerNavigation: DrawerNavigation | undefined;
  emptyState: EmptyStateConfig;
  error: string | null;
  groupOptions: SelectOption[];
  handleCardClick: (alert: RadarAlert) => void;
  handleCategoryChange: (option: SelectOption | null) => void;
  handleCloseDrawer: () => void;
  handleGroupsChange: (options: SelectOption[] | null) => void;
  handleMarkAllRead: () => void;
  handleSortChange: (option: SelectOption | null) => void;
  handleStatusChanged: (annotationId: string, newStatus: string, reviewerName: string) => void;
  hasMore: boolean;
  hasUnread: boolean;
  loading: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  markingAllRead: boolean;
  selectedAlert: RadarAlert | null;
  selectedCategory: SelectOption;
  selectedGroupOptions: SelectOption[];
  selectedSort: SelectOption;
  setActiveTab: (tab: string) => void;
  sortOptions: SelectOption[];
  t: (key: string, opts?: Record<string, unknown>) => string;
  tabs: RadarTab[];
};

function getSeverityFromScore(score: number): RadarSeverity {
  if (score > 0.8) return 'high';
  if (score > 0.5) return 'medium';
  return 'low';
}

function getSeverityFromGroupPercentage(percentage: number): RadarSeverity {
  if (percentage > 0.35) return 'high';
  if (percentage >= 0.25) return 'medium';
  return 'low';
}

function normalizeAnnotation(item: AnnotationItem, t: (key: string, opts?: Record<string, unknown>) => string): RadarAlert {
  if (item.type === ANNOTATION_ITEM_TYPE.FLAGGED_MESSAGE) {
    const msg = item;
    const topCategory = msg.categories?.[0];
    const score = topCategory?.score ?? 0;

    return {
      avatar: null,
      categoryLabel: topCategory?.category ?? t('chatbot:radar_type_inappropriate_use'),
      content: msg.content,
      createdAt: msg.createdAt,
      groupName: '',
      id: msg.annotationId,
      locations: [],
      raw: msg,
      reviewedById: msg.annotationReviewedById,
      reviewedByName: [msg.annotationReviewedByName, msg.annotationReviewedByLastName].filter(Boolean).join(' ') || null,
      severity: getSeverityFromScore(score),
      severityScore: score,
      status: msg.status,
      studentExternalId: msg.studentExternalId,
      studentLastName: msg.studentLastName,
      studentName: msg.studentName,
      type: 'inappropriate-use',
      viewed: msg.viewed,
    };
  }

  if (item.type === ANNOTATION_ITEM_TYPE.GROUP_LEARNING_ALERT) {
    const groupAlert = item;
    const locationStrings = (groupAlert.locations ?? [])
      .map((loc) => {
        const parts = [loc.unit?.name, loc.lesson?.name].filter(Boolean);
        return parts.join(' > ');
      })
      .filter(Boolean);

    return {
      affectedCount: groupAlert.affectedCount,
      avatar: null,
      categoryLabel: t('chatbot:radar_type_learning_block'),
      content: groupAlert.kcStatement,
      createdAt: groupAlert.createdAt,
      groupName: '',
      id: groupAlert.annotationId,
      locations: locationStrings,
      percentage: groupAlert.percentage,
      raw: groupAlert,
      reviewedById: groupAlert.annotationReviewedById,
      reviewedByName: [groupAlert.annotationReviewedByName, groupAlert.annotationReviewedByLastName].filter(Boolean).join(' ') || null,
      severity: getSeverityFromGroupPercentage(groupAlert.percentage),
      severityScore: groupAlert.percentage,
      status: groupAlert.status,
      studentExternalId: groupAlert.courseId,
      studentLastName: null,
      studentName: null,
      totalEnrolled: groupAlert.totalEnrolled,
      type: 'group-learning-alert',
      viewed: groupAlert.viewed,
    };
  }

  const alert = item;
  const locationStrings = (alert.locations ?? [])
    .map((loc) => {
      const parts = [loc.unit?.name, loc.lesson?.name].filter(Boolean);
      return parts.join(' > ');
    })
    .filter(Boolean);

  return {
    avatar: null,
    categoryLabel: t('chatbot:radar_attempts', { count: alert.attemptCount }),
    content: alert.kcStatement,
    createdAt: alert.createdAt,
    groupName: '',
    id: alert.annotationId,
    kcId: alert.kcId,
    locations: locationStrings,
    raw: alert,
    reviewedById: alert.annotationReviewedById,
    reviewedByName: [alert.annotationReviewedByName, alert.annotationReviewedByLastName].filter(Boolean).join(' ') || null,
    severity: getSeverityFromErrorRate(alert.errorRate),
    severityScore: alert.errorRate,
    status: alert.status,
    studentExternalId: alert.studentExternalId,
    studentLastName: alert.studentLastName,
    studentName: alert.studentName,
    type: 'learning-block',
    viewed: alert.viewed,
  };
}

function useRadarView(): UseRadarViewReturn {
  const { i18n, t } = useTranslation(['chatbot']);
  const { allCourseIds, classOptions, groupCourseMap, isMockMode } = useAnalyticsFilters();
  const { studentsMap } = useStudentsMap();
  const effectiveStudentsMap = isMockMode ? MOCK_STUDENTS_MAP : studentsMap;

  const PAGE_SIZE = 25;

  const [allAlerts, setAllAlerts] = useState<RadarAlert[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const offsetRef = useRef<number>(0);
  const [activeTab, setActiveTab] = useState<string>('unattended');
  const [sortBy, setSortBy] = useState<RadarSortOption>('relevance');
  const [markingAllRead, setMarkingAllRead] = useState<boolean>(false);
  const [categoryFilter, setCategoryFilter] = useState<RadarCategoryFilter>('all');
  const [selectedGroupValues, setSelectedGroupValues] = useState<string[] | null>(null);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);

  // --- Filter options ---

  const groupOptions: SelectOption[] = useMemo(() => classOptions.filter((opt) => opt.value !== 'all'), [classOptions]);

  const selectedGroupOptions = useMemo(() => {
    if (selectedGroupValues === null) return groupOptions;
    return groupOptions.filter((opt) => selectedGroupValues.includes(opt.value));
  }, [selectedGroupValues, groupOptions]);

  const sortOptions: SelectOption[] = useMemo(
    () => [
      { label: t('chatbot:radar_sort_relevance'), value: 'relevance' },
      { label: t('chatbot:radar_sort_date_newest'), value: 'date_newest' },
      { label: t('chatbot:radar_sort_date_oldest'), value: 'date_oldest' },
    ],
    [t]
  );

  const selectedSort = useMemo(() => sortOptions.find((opt) => opt.value === sortBy) ?? sortOptions[0], [sortOptions, sortBy]);

  const categoryOptions: SelectOption[] = useMemo(
    () => [
      { label: t('chatbot:radar_filter_all_categories'), value: 'all' },
      { label: t('chatbot:radar_filter_category_learning_block'), value: ANNOTATION_ITEM_TYPE.LEARNING_ALERT },
      { label: t('chatbot:radar_filter_category_group_learning_alert'), value: ANNOTATION_ITEM_TYPE.GROUP_LEARNING_ALERT },
      { label: t('chatbot:radar_filter_category_inappropriate_use'), value: ANNOTATION_ITEM_TYPE.FLAGGED_MESSAGE },
    ],
    [t]
  );

  const selectedCategory = useMemo(() => categoryOptions.find((opt) => opt.value === categoryFilter) ?? categoryOptions[0], [categoryOptions, categoryFilter]);

  // --- Handlers ---

  function handleGroupsChange(options: SelectOption[] | null): void {
    if (!options || options.length === 0) {
      setSelectedGroupValues([]);
    } else if (options.length === groupOptions.length) {
      setSelectedGroupValues(null);
    } else {
      setSelectedGroupValues(options.map((opt) => opt.value));
    }
  }

  function handleSortChange(option: SelectOption | null): void {
    if (option) {
      setSortBy(option.value as RadarSortOption);
    }
  }

  function handleCategoryChange(option: SelectOption | null): void {
    if (option) {
      setCategoryFilter(option.value as RadarCategoryFilter);
    }
  }

  const handleCardClick = useCallback((alert: RadarAlert): void => {
    setSelectedAlertId(alert.id);
  }, []);

  function handleCloseDrawer(): void {
    setSelectedAlertId(null);
  }

  // Mark alert as viewed when selected (covers card click and drawer navigation)
  useEffect(() => {
    if (!selectedAlertId) return;
    const alert = allAlerts.find((a) => a.id === selectedAlertId);
    if (alert && !alert.viewed && !isMockMode) {
      analyticsService.markAnnotationsAsViewed([selectedAlertId]).then(() => {
        setAllAlerts((prev) => prev.map((a) => (a.id === selectedAlertId ? { ...a, viewed: true } : a)));
      });
    }
  }, [selectedAlertId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Data fetching ---

  const activeCourseIds = useMemo(() => {
    if (!selectedGroupValues || selectedGroupValues.length === 0) return allCourseIds;
    const ids = selectedGroupValues.flatMap((groupValue) => groupCourseMap[groupValue] ?? []);
    return Array.from(new Set(ids));
  }, [selectedGroupValues, allCourseIds, groupCourseMap]);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    offsetRef.current = 0;
    setHasMore(true);

    if (isMockMode) {
      const normalized = getMockAnnotations(i18n.language).map((item) => normalizeAnnotation(item, t));
      setAllAlerts(normalized);
      setHasMore(false);
      setLoading(false);
      return;
    }

    try {
      const courseIds = activeCourseIds;

      if (courseIds.length === 0) {
        setAllAlerts([]);
        setLoading(false);
        return;
      }

      const data: AnnotationsResponse = await analyticsService.getAnnotations({
        courseIds,
        limit: PAGE_SIZE,
        offset: 0,
      });

      const normalized = data.items.map((item) => normalizeAnnotation(item, t));
      setAllAlerts(normalized);
      setHasMore(data.pagination.hasMore);
      offsetRef.current = PAGE_SIZE;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setAllAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [activeCourseIds, t, isMockMode]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);

    try {
      const data: AnnotationsResponse = await analyticsService.getAnnotations({
        courseIds: activeCourseIds,
        limit: PAGE_SIZE,
        offset: offsetRef.current,
      });

      const normalized = data.items.map((item) => normalizeAnnotation(item, t));
      setAllAlerts((prev) => [...prev, ...normalized]);
      setHasMore(data.pagination.hasMore);
      offsetRef.current += PAGE_SIZE;
    } catch {
      // Silent fail — keep current alerts
    } finally {
      setLoadingMore(false);
    }
  }, [activeCourseIds, hasMore, loadingMore, t]);

  useEffect(() => {
    if (allCourseIds.length > 0) {
      fetchAlerts();
    } else {
      setLoading(false);
      setAllAlerts([]);
    }
  }, [fetchAlerts, activeCourseIds.length]);

  // --- Enrich alerts with student info from studentsMap ---

  // Map courseId → group/class label, derived from classOptions + groupCourseMap
  const courseIdToGroupName = useMemo(() => {
    const map = new Map<string, string>();
    classOptions.forEach((option) => {
      if (option.value === 'all') return;
      const courseIds = groupCourseMap[option.value] ?? [];
      courseIds.forEach((courseId) => {
        map.set(courseId, option.label);
      });
    });
    if (isMockMode) {
      Object.entries(MOCK_COURSE_ID_TO_GROUP_NAME).forEach(([courseId, groupName]) => {
        map.set(courseId, groupName);
      });
    }
    return map;
  }, [classOptions, groupCourseMap, isMockMode]);

  const enrichedAlerts = useMemo(() => {
    return allAlerts.map((alert) => {
      if (alert.type === 'group-learning-alert') {
        const groupName = courseIdToGroupName.get(alert.studentExternalId);
        return groupName ? { ...alert, groupName } : alert;
      }

      const info = effectiveStudentsMap.get(alert.studentExternalId);
      if (!info) return alert;

      return {
        ...alert,
        avatar: info.avatar,
        groupName: info.groupName,
        studentName: info.name || alert.studentName,
        studentLastName: info.lastName || alert.studentLastName,
      };
    });
  }, [allAlerts, effectiveStudentsMap, courseIdToGroupName]);

  // --- Filtering & sorting ---

  const categoryFiltered = useMemo(() => {
    if (categoryFilter === 'all') return enrichedAlerts;
    if (categoryFilter === ANNOTATION_ITEM_TYPE.FLAGGED_MESSAGE) return enrichedAlerts.filter((a) => a.type === 'inappropriate-use');
    if (categoryFilter === ANNOTATION_ITEM_TYPE.LEARNING_ALERT) return enrichedAlerts.filter((a) => a.type === 'learning-block');
    if (categoryFilter === ANNOTATION_ITEM_TYPE.GROUP_LEARNING_ALERT) return enrichedAlerts.filter((a) => a.type === 'group-learning-alert');
    // DISCOVERY: no alerts of this type yet, return empty
    return [];
  }, [enrichedAlerts, categoryFilter]);

  const counts = useMemo(() => {
    const unattended = categoryFiltered.filter((a) => a.status === RADAR_ALERT_STATUS.PENDING).length;
    const attended = categoryFiltered.filter((a) => a.status === RADAR_ALERT_STATUS.REVIEWED || a.status === RADAR_ALERT_STATUS.RESOLVED).length;
    const dismissed = categoryFiltered.filter((a) => a.status === RADAR_ALERT_STATUS.DISMISSED).length;
    return { unattended, attended, dismissed };
  }, [categoryFiltered]);

  const tabs: RadarTab[] = useMemo(
    () => [
      { id: 'unattended', label: t('chatbot:radar_tab_unattended'), count: counts.unattended },
      { id: 'attended', label: t('chatbot:radar_tab_attended'), count: counts.attended },
      { id: 'dismissed', label: t('chatbot:radar_tab_dismissed'), count: counts.dismissed },
    ],
    [t, counts]
  );

  const alerts = useMemo(() => {
    let filtered: RadarAlert[];
    switch (activeTab) {
      case 'unattended':
        filtered = categoryFiltered.filter((a) => a.status === RADAR_ALERT_STATUS.PENDING);
        break;
      case 'attended':
        filtered = categoryFiltered.filter((a) => a.status === RADAR_ALERT_STATUS.REVIEWED || a.status === RADAR_ALERT_STATUS.RESOLVED);
        break;
      case 'dismissed':
        filtered = categoryFiltered.filter((a) => a.status === RADAR_ALERT_STATUS.DISMISSED);
        break;
      default:
        filtered = categoryFiltered;
    }

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date_newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'date_oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'relevance':
        default: {
          const severityOrder: Record<RadarSeverity, number> = { high: 0, medium: 1, low: 2 };
          const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
          if (severityDiff !== 0) return severityDiff;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }
      }
    });
  }, [categoryFiltered, activeTab, sortBy]);

  const hasUnread = useMemo(() => alerts.some((a) => !a.viewed), [alerts]);

  const emptyState: EmptyStateConfig = useMemo(() => {
    switch (activeTab) {
      case 'attended':
        return {
          iconType: 'alert',
          iconVariant: 'warning',
          title: t('chatbot:radar_empty_attended_title'),
          description: t('chatbot:radar_empty_attended_description'),
        };
      case 'dismissed':
        return {
          iconType: 'alert',
          iconVariant: 'muted',
          title: t('chatbot:radar_empty_dismissed_title'),
          description: t('chatbot:radar_empty_dismissed_description'),
        };
      default:
        return {
          iconType: 'check',
          iconVariant: 'success',
          title: t('chatbot:radar_empty_unattended_title'),
          description: t('chatbot:radar_empty_unattended_description'),
        };
    }
  }, [activeTab, t]);

  // --- Drawer navigation ---

  const selectedAlertIndex = useMemo(() => {
    if (!selectedAlertId) return -1;
    return alerts.findIndex((a) => a.id === selectedAlertId);
  }, [selectedAlertId, alerts]);

  const selectedAlert = useMemo((): RadarAlert | null => {
    if (selectedAlertIndex === -1) return null;
    return alerts[selectedAlertIndex] ?? null;
  }, [selectedAlertIndex, alerts]);

  const handleNextAlert = useCallback((): void => {
    if (selectedAlertIndex < alerts.length - 1) {
      setSelectedAlertId(alerts[selectedAlertIndex + 1].id);
    }
  }, [selectedAlertIndex, alerts]);

  const handlePreviousAlert = useCallback((): void => {
    if (selectedAlertIndex > 0) {
      setSelectedAlertId(alerts[selectedAlertIndex - 1].id);
    }
  }, [selectedAlertIndex, alerts]);

  const drawerNavigation = useMemo((): DrawerNavigation | undefined => {
    if (selectedAlertIndex === -1) return undefined;
    return {
      currentIndex: selectedAlertIndex,
      onNext: handleNextAlert,
      onPrevious: handlePreviousAlert,
      totalCount: alerts.length,
    };
  }, [selectedAlertIndex, alerts.length, handleNextAlert, handlePreviousAlert]);

  // H4 fix: add catch for error handling
  const handleMarkAllRead = useCallback(async () => {
    if (isMockMode) return;
    const unreadIds = alerts.filter((a) => !a.viewed).map((a) => a.id);
    if (unreadIds.length === 0) return;

    setMarkingAllRead(true);
    try {
      await analyticsService.markAnnotationsAsViewed(unreadIds);
      await fetchAlerts();
    } catch {
      // Silent fail — the alerts will remain unread visually
    } finally {
      setMarkingAllRead(false);
    }
  }, [alerts, fetchAlerts]);

  const handleStatusChanged = useCallback(
    (_annotationId: string, _newStatus: string, _reviewerName: string) => {
      setSelectedAlertId(null);
      fetchAlerts();
    },
    [fetchAlerts]
  );

  return {
    activeTab,
    alerts,
    categoryOptions,
    drawerNavigation,
    emptyState,
    error,
    groupOptions,
    handleCardClick,
    handleCategoryChange,
    handleCloseDrawer,
    handleGroupsChange,
    handleMarkAllRead,
    handleSortChange,
    handleStatusChanged,
    hasMore,
    hasUnread,
    loading,
    loadingMore,
    loadMore,
    markingAllRead,
    selectedAlert,
    selectedCategory,
    selectedGroupOptions,
    selectedSort,
    setActiveTab,
    sortOptions,
    t,
    tabs,
  };
}

export default useRadarView;
export type { RadarCategoryFilter, RadarSortOption, RadarTab, SelectOption };
